import React, { useEffect, useState } from 'react';
import { Container, Card, CardBody, CardHeader, Row, Col, Input } from 'reactstrap';
import SnackbarContent from '@mui/material/SnackbarContent';
import LinearProgress from '@mui/material/LinearProgress';
import { IconButton, Tooltip, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import PndRestApi from 'src/utils/pnd/PndRestApi';
import Report from './SecurityPerformanceReport';
import { showNotification } from 'src/shared/components/Notification';
import { ERROR_MESSAGES } from 'src/utils/pnd/PndConstants';

const ReportIndex = () => {
    const [loading, setLoading] = useState(false);
    const [isNoRecordFound, setNoRecordFound] = useState(false);
    const [isReport, setReports] = useState(null);
    const [exportData, setExportData] = useState([]);
    const [hubs, setHubs] = useState([]);

    // Filter states
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [selectedHub, setSelectedHub] = useState('');
    const [securityStatus, setSecurityStatus] = useState('');
    const [search, setSearch] = useState(''); // Security Name filter

    // Pagination states
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);

    const clearFilters = () => {
        setFromDate('');
        setToDate('');
        setSelectedHub('');
        setSecurityStatus('');
        setSearch('');
        setReports(null);
        setExportData([]);
        setNoRecordFound(false);
        setTotalElements(0);
        setCurrentPage(0);
    };

    useEffect(() => {
        fetchHubs();
    }, []);

    const fetchHubs = async () => {
        try {
            const response = await PndRestApi.GetAllActiveHub();
            if (response.status === 200 && response.data.status === 200) {
                setHubs(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching hubs:', error);
        }
    };

    const generateSecurityPerformanceReport = async (page = 0, size = pageSize) => {
        try {
            setLoading(true);
            setNoRecordFound(false);
            if (page === 0) {
                setReports(null);
                setExportData([]);
            }

            const payload = {
                fromDate: fromDate || null,
                toDate: toDate || null,
                selectedHubId: selectedHub || null,
                securityApprovalStatus: securityStatus !== '' ? parseInt(securityStatus) : null,
                securityName: search || null,
                page: page,
                size: size,
                hubId: localStorage.getItem("hi"),
                userTypeId: localStorage.getItem("uri")
            };

            const response = await PndRestApi.GenerateSecurityPerformanceReport(payload);
            if (response.status === 200 && response.data.status === 200) {
                const data = response.data.data;
                if (data.content) {
                    setReports(data.content);
                    setTotalElements(data.totalElements || 0);
                    setCurrentPage(data.currentPage || page);

                    if (page === 0 && data.totalElements > 0) {
                        fetchFullDataForExport(payload);
                    }
                } else {
                    setReports(data);
                    setTotalElements(data.length || 0);
                    setExportData(data);
                }
            } else if (response?.data?.status && response?.data?.status === 204) {
                setNoRecordFound(true);
                setReports(null);
                setTotalElements(0);
                setExportData([]);
            } else {
                showNotification(
                    "theme-light",
                    "ltr",
                    `Error: ${response.data.status}`,
                    `Message: ${response.data.message}`,
                );
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            showNotification(
                "theme-light",
                "ltr",
                `Error: 500`,
                ERROR_MESSAGES.tryCatchError,
            );
        } finally {
            setLoading(false);
        }
    };

    const fetchFullDataForExport = async (basePayload) => {
        try {
            const exportPayload = { ...basePayload, page: 0, size: 3000 };
            const response = await PndRestApi.GenerateSecurityPerformanceReport(exportPayload);
            if (response.status === 200 && response.data.status === 200) {
                const data = response.data.data;
                setExportData(data.content || data);
            }
        } catch (error) {
            console.error('Export fetch error:', error);
        }
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        generateSecurityPerformanceReport(newPage, pageSize);
    };

    const handleRowsPerPageChange = (newSize) => {
        setPageSize(newSize);
        setCurrentPage(0);
        generateSecurityPerformanceReport(0, newSize);
    };

    return (
        <Container fluid>
            <Card className="border-0 shadow-sm mb-4">
                <CardHeader className="bg-white py-3">
                    <h5 className="mb-0 fw-bold">Security Performance Report Filters</h5>
                </CardHeader>
                <CardBody>
                    <Row className="g-3">
                        <Col md={3} lg={2}>
                            <label className="form-label small fw-bold text-muted">From Date</label>
                            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                        </Col>
                        <Col md={3} lg={2}>
                            <label className="form-label small fw-bold text-muted">To Date</label>
                            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                        </Col>
                        <Col md={3} lg={2}>
                            <label className="form-label small fw-bold text-muted">Store (Hub)</label>
                            <Input type="select" value={selectedHub} onChange={(e) => setSelectedHub(e.target.value)}>
                                <option value="">All Stores</option>
                                {hubs.map(hub => (
                                    <option key={hub.hubId} value={hub.hubId}>{hub.hubName}</option>
                                ))}
                            </Input>
                        </Col>
                        <Col md={3} lg={2}>
                            <label className="form-label small fw-bold text-muted">Approval Status</label>
                            <Input type="select" value={securityStatus} onChange={(e) => setSecurityStatus(e.target.value)}>
                                <option value="">All Status</option>
                                <option value="1">Approved</option>
                                <option value="2">Declined</option>
                                <option value="0">Pending</option>
                            </Input>
                        </Col>
                        <Col md={3} lg={3}>
                            <label className="form-label small fw-bold text-muted">Security Name</label>
                            <Input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search by name..."
                            />
                        </Col>
                        <Col md={3} lg={1} className="d-flex align-items-end gap-1">
                            <Tooltip title="Clear Filters" arrow>
                                <IconButton
                                    onClick={clearFilters}
                                    sx={{
                                        backgroundColor: '#f8f9fa',
                                        border: '1px solid #dee2e6',
                                        '&:hover': { backgroundColor: '#e2e6ea' }
                                    }}
                                >
                                    <RestartAltIcon color="action" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Generate Report" arrow>
                                <IconButton
                                    color="primary"
                                    onClick={() => generateSecurityPerformanceReport(0, pageSize)}
                                    disabled={loading}
                                    sx={{
                                        backgroundColor: 'primary.main',
                                        color: 'white',
                                        '&:hover': { backgroundColor: 'primary.dark' },
                                        '&.Mui-disabled': { backgroundColor: '#ccc' }
                                    }}
                                >
                                    {loading ? <CircularProgress size={24} color="inherit" /> : <SearchIcon />}
                                </IconButton>
                            </Tooltip>
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {loading && <LinearProgress color="primary" className="mb-3" />}
            {isReport && (
                <Report
                    isReport={isReport}
                    page={currentPage}
                    rowsPerPage={pageSize}
                    totalElements={totalElements}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    exportReport={exportData}
                />
            )}
            {isNoRecordFound && <SnackbarContent message={"No Record Found"} />}
        </Container>
    );
};

export default ReportIndex;
